<template>
  <div>
  <b-modal
    id="create-subscription-modal"
    ref="modal"
    :title="stepTitle"
    size="lg"
    hide-footer
    no-close-on-backdrop
    @hidden="resetForm"
  >

    <!-- ── STEP INDICATOR ── -->
    <div class="d-flex flex-wrap align-items-center mb-4">
      <div
        v-for="(label, i) in steps"
        :key="i"
        class="d-flex align-items-center"
      >
        <div
          class="rounded-circle d-flex align-items-center justify-content-center"
          style="width:28px;height:28px;font-size:12px;"
          :class="step > i + 1
            ? 'bg-primary text-white'
            : step === i + 1
              ? 'bg-primary text-white'
              : 'bg-light text-muted border'"
        >
          <i v-if="step > i + 1" class="fas fa-check" style="font-size:10px;"></i>
          <span v-else>{{ i + 1 }}</span>
        </div>
        <span
          class="ml-1 mr-3"
          :class="step === i + 1 ? 'font-weight-bold' : 'text-muted'"
        >
          {{ label }}
        </span>
        <div v-if="i < steps.length - 1" class="border-top flex-grow-1 mr-3" style="width:30px;"></div>
      </div>
    </div>

    <!-- ================================================================
         STEP 1 — SUBSCRIPTION DETAILS
         ================================================================ -->
    <div v-if="step === 1">

      <b-row>
        <b-col md="6">
          <b-form-group>
            <template #label>
              Customer *
              <a
                href="#"
                class="ml-2"
                style="font-weight:normal;"
                @click.prevent="showNewCustomerModal"
              >
                <i class="fas fa-plus-circle"></i> New customer
              </a>
            </template>
            <customer-search-select
              ref="customerSearch"
              v-model="form.customer_id"
              :class="errors.customer_id ? 'is-invalid' : ''"
            />
            <div v-if="errors.customer_id" class="invalid-feedback d-block">{{ errors.customer_id }}</div>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group>
            <template #label>
              Package *
              <a
                v-if="can('subscriptions.manage_packages')"
                href="#"
                class="ml-2"
                style="font-weight:normal;"
                @click.prevent="$refs.newPackageModal.show()"
              >
                <i class="fas fa-plus-circle"></i> New package
              </a>
            </template>
            <b-form-select
              v-model="form.subscription_package_id"
              :options="packageOptions"
              :state="errors.subscription_package_id ? false : null"
            />
            <b-form-invalid-feedback>{{ errors.subscription_package_id }}</b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Package summary + participant count -->
      <div v-if="selectedPackage" class="alert alert-secondary mb-3 py-2">
        <b-row class="align-items-center">
          <b-col>
            <strong>{{ selectedPackage.name }}</strong>
            <span class="text-muted ml-2">
              {{ selectedPackage.duration_value }} {{ selectedPackage.duration_unit }}(s)
            </span>
          </b-col>
          <b-col cols="auto">
            <strong class="text-primary">KES {{ formatAmount(selectedPackage.price) }}</strong>
            <span class="text-muted ml-1">/ person</span>
          </b-col>
        </b-row>

        <!-- Participant count stepper -->
        <b-row class="align-items-center mt-2">
          <b-col cols="12" sm="6">
            <small class="text-muted">Number of participants</small>
          </b-col>
          <b-col cols="12" sm="6">
            <div class="d-flex align-items-center">
              <b-button size="sm" variant="outline-secondary" style="width:30px;padding:0;"
                        :disabled="form.participant_count <= 1"
                        @click="form.participant_count = Math.max(1, form.participant_count - 1)">
                &minus;
              </b-button>
              <span class="mx-2 font-weight-bold" style="min-width:20px;text-align:center;">
                {{ form.participant_count }}
              </span>
              <b-button size="sm" variant="outline-secondary" style="width:30px;padding:0;"
                        :disabled="maxParticipants && form.participant_count >= maxParticipants"
                        @click="form.participant_count++">
                +
              </b-button>
              <small v-if="maxParticipants" class="text-muted ml-2">max {{ maxParticipants }}</small>
            </div>
          </b-col>
        </b-row>
      </div>

      <!-- Discount row -->
      <div v-if="selectedPackage" class="mb-3">
        <b-row class="align-items-center">
          <b-col cols="12" sm="6">
            <small class="text-muted">Discount (optional)</small>
          </b-col>
          <b-col cols="12" sm="6">
            <div class="d-flex align-items-center">
              <b-form-select
                v-model="form.discount_type"
                :options="[{value:'flat',text:'KES'},{value:'pct',text:'%'}]"
                size="sm"
                style="width:70px;"
                class="mr-2"
              />
              <b-form-input
                v-model.number="form.discount_value"
                type="number"
                min="0"
                size="sm"
                placeholder="0"
                style="width:90px;"
              />
            </div>
          </b-col>
        </b-row>
      </div>

      <!-- Live price preview -->
      <div v-if="selectedPackage && (form.participant_count > 1 || pricePreviewDiscount > 0)"
           class="alert alert-light border mb-3 py-2">
        <div class="d-flex justify-content-between">
          <span class="text-muted">{{ form.participant_count }} × KES {{ formatAmount(selectedPackage.price) }}</span>
          <span>KES {{ formatAmount(pricePreviewSubtotal) }}</span>
        </div>
        <div v-if="pricePreviewDiscount > 0" class="d-flex justify-content-between text-success">
          <span>Discount</span>
          <span>&minus; KES {{ formatAmount(pricePreviewDiscount) }}</span>
        </div>
        <div class="d-flex justify-content-between font-weight-bold border-top mt-1 pt-1">
          <span>Total</span>
          <span class="text-primary">KES {{ formatAmount(pricePreviewTotal) }}</span>
        </div>
      </div>

      <!-- Wallet balance for selected customer -->
      <div v-if="form.customer_id && walletBalance !== null" class="alert alert-secondary mb-3 py-2">
        <b-row class="align-items-center">
          <b-col>
            <i class="fas fa-wallet mr-2 text-muted"></i>
            <span class="text-muted">Wallet Balance</span>
          </b-col>
          <b-col cols="auto">
            <strong>KES {{ formatAmount(walletBalance) }}</strong>
          </b-col>
        </b-row>
      </div>

      <b-row>
        <b-col md="6">
          <b-form-group label="Start Date">
            <date-range-picker :single-date-picker="true" :auto-apply="true" :ranges="false" :append-to-body="true" v-model="startDatePicker" class="custom-width-datepicker">
              <template #input="picker">
                <span v-if="picker.startDate" class="text-dark">{{ formatPickerDate(picker.startDate) }}</span>
                <span v-else class="text-muted"><i class="bi bi-calendar-event mr-1"></i>Select date</span>
              </template>
            </date-range-picker>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="&nbsp;">
            <b-form-checkbox v-model="form.is_auto_renew" class="mt-2">
              Enable auto-renewal
            </b-form-checkbox>
          </b-form-group>
        </b-col>
      </b-row>

      <b-form-group label="Notes">
        <b-form-textarea v-model="form.notes" rows="2" />
      </b-form-group>

      <b-form-checkbox v-model="form.send_welcome_sms" class="mb-2">
        Send welcome SMS with the member app link
        <small class="text-secondary d-block">
          Uses 1 SMS. Turn on by default under Subscriptions &rsaquo; Overview.
        </small>
      </b-form-checkbox>

      <div class="d-flex justify-content-end mt-2">
        <b-button variant="outline-secondary" class="mr-2" @click="$refs.modal.hide()">
          Cancel
        </b-button>
        <b-button variant="primary" :disabled="creatingSubscription" @click="createSubscription">
          <b-spinner v-if="creatingSubscription" small class="mr-1" />
          Next — Payment
        </b-button>
      </div>
    </div>

    <!-- ================================================================
         STEP 2 — PAYMENT
         ================================================================ -->
    <div v-if="step === 2">

      <!-- Amount summary bar -->
      <b-card class="mb-3 border-0 bg-light">
        <b-row class="text-center">
          <b-col cols="12" sm="4" class="mb-2 mb-sm-0">
            <p class="text-muted mb-1">Amount Due</p>
            <h5 class="font-weight-bold mb-0">KES {{ formatAmount(amountDue) }}</h5>
          </b-col>
          <b-col cols="12" sm="4" class="mb-2 mb-sm-0">
            <p class="text-muted mb-1">Total Paid</p>
            <h5 class="font-weight-bold mb-0 text-primary">KES {{ formatAmount(totalPaid) }}</h5>
          </b-col>
          <b-col cols="12" sm="4" class="mb-2 mb-sm-0">
            <p class="text-muted mb-1">
              {{ remaining > 0 ? 'Remaining' : overpayment > 0 ? 'Overpayment' : 'Settled' }}
            </p>
            <h5
              class="font-weight-bold mb-0"
              :class="remaining > 0 ? 'text-danger' : overpayment > 0 ? 'text-primary' : 'text-secondary'"
            >
              <span v-if="remaining > 0">KES {{ formatAmount(remaining) }}</span>
              <span v-else-if="overpayment > 0">KES {{ formatAmount(overpayment) }}</span>
              <span v-else><i class="fas fa-check text-secondary"></i></span>
            </h5>
          </b-col>
        </b-row>
      </b-card>

      <!-- Posted payments so far -->
      <div v-if="postedPayments.length" class="mb-3">
        <p class="text-muted font-weight-bold mb-1">PAYMENTS RECORDED</p>
        <div
          v-for="(p, i) in postedPayments"
          :key="i"
          class="d-flex justify-content-between border-bottom py-1"
        >
          <span>{{ p.method_label }}</span>
          <strong>KES {{ formatAmount(p.amount) }}</strong>
        </div>
      </div>

      <!-- Payment input — shown when not yet fully paid -->
      <div v-if="remaining > 0 && !showWalletPrompt">

        <b-row>
          <b-col md="5">
            <b-form-group label="Payment Method *">
              <b-form-select
                v-model="paymentLine.payment_type_id"
                :options="paymentMethods"
                :state="paymentErrors.payment_type_id ? false : null"
              />
              <b-form-invalid-feedback>{{ paymentErrors.payment_type_id }}</b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Amount (KES) *">
              <b-form-input
                v-model.number="paymentLine.amount"
                type="number"
                min="0.01"
                :placeholder="formatAmount(remaining)"
                :state="paymentErrors.amount ? false : null"
                @keyup.enter="postPaymentLine"
              />
              <b-form-invalid-feedback>{{ paymentErrors.amount }}</b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col md="3" class="d-flex align-items-end pb-3">
            <b-button
              variant="primary"
              :disabled="postingPayment || !paymentLine.payment_type_id || !paymentLine.amount"
              @click="postPaymentLine"
            >
              <b-spinner v-if="postingPayment" class="mr-1" />
              Add
            </b-button>
          </b-col>
        </b-row>

        <!-- Wallet option — shown if customer has balance and there's still remaining -->
        <div v-if="walletBalance > 0" class="mt-1">
          <b-form-checkbox v-model="useWallet" @change="onWalletToggle">
            Use wallet balance
            <span class="text-muted ml-1">(KES {{ formatAmount(walletBalance) }} available)</span>
          </b-form-checkbox>
        </div>
      </div>

      <!-- Wallet confirmation — shown when useWallet is checked -->
      <div v-if="useWallet && remaining > 0 && showWalletPrompt">
        <b-card class="border-0 bg-light mb-3">
          <p class="mb-1 font-weight-bold">Pay remaining with wallet?</p>
          <b-row class="align-items-center">
            <b-col>
              <span class="text-muted">
                Wallet balance: KES {{ formatAmount(walletBalance) }}<br />
                Amount to deduct: KES {{ formatAmount(Math.min(remaining, walletBalance)) }}
              </span>
            </b-col>
            <b-col cols="auto">
              <b-button
                variant="primary"
                size="sm"
                :disabled="postingPayment"
                @click="postWalletPayment"
              >
                <b-spinner v-if="postingPayment" small class="mr-1" />
                Confirm
              </b-button>
              <b-button
                variant="outline-secondary"
                size="sm"
                class="ml-1"
                @click="useWallet = false; showWalletPrompt = false"
              >
                Cancel
              </b-button>
            </b-col>
          </b-row>
        </b-card>
      </div>

      <!-- Overpayment prompt -->
      <div v-if="overpayment > 0 && !overpaymentHandled">
        <b-card class="border-0 bg-light mb-3">
          <p class="font-weight-bold mb-1">
            <i class="fas fa-exclamation-circle text-primary mr-1"></i>
            Overpayment of KES {{ formatAmount(overpayment) }}
          </p>
          <p class="text-muted mb-2">
            The amount paid exceeds the subscription fee. Load the difference to the customer's wallet?
          </p>
          <div class="d-flex flex-wrap">
            <b-button
              variant="primary"
              size="sm"
              class="mb-1"
              :disabled="loadingOverpayment"
              @click="loadOverpaymentToWallet"
            >
              <b-spinner v-if="loadingOverpayment" small class="mr-1" />
              Yes, Load KES {{ formatAmount(overpayment) }} to Wallet
            </b-button>
            <b-button
              variant="outline-secondary"
              size="sm"
              class="ml-2"
              @click="overpaymentHandled = true"
            >
              Skip
            </b-button>
          </div>
        </b-card>
      </div>

      <!-- Fully paid — complete button -->
      <div
        v-if="remaining <= 0 && (overpayment === 0 || overpaymentHandled)"
        class="d-flex justify-content-end mt-3"
      >
        <b-button variant="primary" @click="complete">
          <i class="fas fa-check mr-1"></i> Complete
        </b-button>
      </div>

      <!-- Skip payment — subscription is already created, allow finishing without paying now -->
      <div v-if="remaining > 0" class="d-flex flex-wrap justify-content-between align-items-center mt-3 pt-2 border-top">
        <small class="text-muted mb-1 mr-2">
          <i class="fas fa-info-circle mr-1"></i>
          The subscription is created. You can collect payment later.
        </small>
        <b-button variant="outline-secondary" size="sm" @click="skipPayment">
          Skip for now
        </b-button>
      </div>

    </div>

  </b-modal>

  <!-- ── NEW PACKAGE MODAL ── -->
  <b-modal
    ref="newPackageModal"
    title="New Package"
    hide-footer
    no-close-on-backdrop
    @hidden="resetPackageForm"
  >
    <div v-if="packageFormError" class="alert alert-danger py-2">{{ packageFormError }}</div>
    <form @submit.prevent="createPackage">
      <b-form-group label="Name *">
        <b-form-input v-model="packageForm.name" placeholder="e.g. Monthly Gym" required />
      </b-form-group>
      <b-form-group description="Links the package to an item so receipts use its VAT class and HS code (eTIMS).">
        <template #label>Product</template>
        <div v-if="packageForm.product_id" class="input-group">
          <input type="text" class="form-control" :value="packageProductName" readonly />
          <div class="input-group-append">
            <button type="button" class="btn btn-outline-secondary" title="Change" @click="clearPackageProduct">
              <i class="bi bi-x"></i>
            </button>
          </div>
        </div>
        <div v-else class="position-relative">
          <input
            v-model="productSearch"
            type="text"
            class="form-control"
            placeholder="Search products…"
            autocomplete="off"
            @input="onProductSearch"
          />
          <div v-if="productSearch.length >= 2" class="list-group position-absolute w-100 shadow-sm" style="z-index:1060;max-height:220px;overflow-y:auto;">
            <div v-if="searchingProducts" class="list-group-item text-secondary small">Searching…</div>
            <template v-else>
              <button
                v-for="p in productResults"
                :key="p.id"
                type="button"
                class="list-group-item list-group-item-action"
                @click="pickPackageProduct(p)"
              >{{ p.product }}</button>
              <div v-if="!productResults.length" class="list-group-item text-secondary small">No products match.</div>
            </template>
          </div>
        </div>
      </b-form-group>
      <b-form-group label="Price (KES) *">
        <b-form-input v-model.number="packageForm.price" type="number" min="0" step="0.01" required />
      </b-form-group>
      <b-row>
        <b-col cols="5">
          <b-form-group label="Lasts *">
            <b-form-input v-model.number="packageForm.duration_value" type="number" min="1" required />
          </b-form-group>
        </b-col>
        <b-col cols="7">
          <b-form-group label="&nbsp;">
            <b-form-select v-model="packageForm.duration_unit" :options="durationUnits" />
          </b-form-group>
        </b-col>
      </b-row>
      <div class="d-flex justify-content-end mt-2">
        <b-button variant="outline-secondary" class="mr-2" @click="$refs.newPackageModal.hide()">Cancel</b-button>
        <b-button
          type="submit"
          variant="primary"
          :disabled="creatingPackage || !packageForm.name || packageForm.price === '' || packageForm.price === null || !packageForm.duration_value"
        >
          <b-spinner v-if="creatingPackage" small class="mr-1" />
          Create Package
        </b-button>
      </div>
    </form>
  </b-modal>

  <!-- ── NEW CUSTOMER MODAL ── -->
  <b-modal
    ref="newCustomerModal"
    title="New Customer"
    size="md"
    hide-footer
    no-close-on-backdrop
    @hidden="resetCustomerForm"
  >
    <div v-if="customerFormError" class="alert alert-danger py-2">
      {{ customerFormError }}
    </div>

    <form @submit.prevent="createCustomer">
      <b-row>
        <b-col cols="12" sm="6">
          <b-form-group label="First Name *">
            <b-form-input
              v-model="customerForm.f_name"
              placeholder="First name"
              required
            />
          </b-form-group>
        </b-col>
        <b-col cols="12" sm="6">
          <b-form-group label="Last Name">
            <b-form-input
              v-model="customerForm.l_name"
              placeholder="Last name"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-form-group label="Phone Number">
        <b-form-input
          v-model="customerForm.phone_no"
          placeholder="e.g. 0712345678"
        />
      </b-form-group>

      <b-form-group label="Email">
        <b-form-input
          v-model="customerForm.c_email"
          type="email"
          placeholder="Email address"
        />
      </b-form-group>

      <b-row>
        <b-col cols="12" sm="6">
          <b-form-group label="KRA PIN">
            <b-form-input
              v-model="customerForm.krapin"
              placeholder="KRA PIN"
            />
          </b-form-group>
        </b-col>
        <b-col cols="12" sm="6">
          <b-form-group label="Company Name">
            <b-form-input
              v-model="customerForm.company"
              placeholder="Company name"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <div class="d-flex justify-content-end mt-3">
        <b-button
          variant="outline-secondary"
          class="mr-2"
          @click="$refs.newCustomerModal.hide()"
        >
          Cancel
        </b-button>
        <b-button
          type="submit"
          variant="primary"
          :disabled="creatingCustomer || !customerForm.f_name"
        >
          <b-spinner v-if="creatingCustomer" small class="mr-1" />
          <i v-else class="fas fa-user-plus mr-1"></i>
          Create Customer
        </b-button>
      </div>
    </form>
  </b-modal>

  </div>
</template>

<script>
import moment from 'moment'
import DateRangePicker from 'vue2-daterange-picker/src/components/DateRangePicker'
import CustomerSearchSelect from '../partials/CustomerSearchSelect.vue'

export default {
  name: 'CreateSubscriptionModal',

  components: { CustomerSearchSelect, DateRangePicker },

  data() {
    return {
      step: 1,
      steps: ['Details', 'Payment'],

      // Step 1 — subscription details
      form: {
        customer_id:              null,
        subscription_package_id:  null,
        start_date:               null,
        is_auto_renew:            false,
        notes:                    null,
        participant_count:        1,
        discount_type:            'flat',
        discount_value:           0,
        send_welcome_sms:         false,
      },
      welcomeSmsDefault:   false,
      errors:              {},
      creatingSubscription: false,

      // Loaded data
      packageOptions:   [{ value: null, text: '— Select package —' }],
      packageForm:      { name: '', product_id: null, price: null, duration_value: 1, duration_unit: 'month' },
      packageProductName: '',
      productSearch:      '',
      productResults:     [],
      searchingProducts:  false,
      productSearchTimer: null,
      packageFormError: '',
      creatingPackage:  false,
      durationUnits:    [
        { value: 'day', text: 'Day(s)' },
        { value: 'week', text: 'Week(s)' },
        { value: 'month', text: 'Month(s)' },
        { value: 'year', text: 'Year(s)' },
      ],
      paymentMethods:   [{ value: null, text: '— Select method —' }],
      packages:         [], // full package objects for price lookup
      walletBalance:    null,

      // Step 2 — payment
      subscriptionId:   null,
      amountDue:        0,
      totalPaid:        0,

      paymentLine: {
        payment_type_id: null,
        amount:          null,
      },
      paymentErrors:  {},
      postingPayment: false,
      postedPayments: [], // { method_label, amount }

      // Wallet
      useWallet:        false,
      showWalletPrompt: false,

      // Overpayment
      overpaymentHandled: false,
      loadingOverpayment: false,

      // New customer inline form
      customerForm: {
        f_name:   '',
        l_name:   '',
        phone_no: '',
        c_email:  '',
        krapin:   '',
        company:  '',
      },
      creatingCustomer:  false,
      customerFormError: null,
    }
  },

  computed: {
    startDatePicker: {
      get() { return { startDate: this.form.start_date ? new Date(this.form.start_date) : null }; },
      set(val) { this.form.start_date = val && val.startDate ? moment(val.startDate).format('YYYY-MM-DD') : null; },
    },
    stepTitle() {
      return this.step === 1 ? 'Create Subscription' : 'Payment'
    },

    selectedPackage() {
      if (!this.form.subscription_package_id) return null
      return this.packages.find(p => p.id === this.form.subscription_package_id) || null
    },

    maxParticipants() {
      return this.selectedPackage?.max_participants || null
    },

    pricePreviewSubtotal() {
      if (!this.selectedPackage) return 0
      return parseFloat(this.selectedPackage.price || 0) * (this.form.participant_count || 1)
    },

    pricePreviewDiscount() {
      if (!this.selectedPackage) return 0
      const v = parseFloat(this.form.discount_value || 0)
      if (this.form.discount_type === 'pct') {
        return Math.min(this.pricePreviewSubtotal, this.pricePreviewSubtotal * v / 100)
      }
      return Math.min(this.pricePreviewSubtotal, Math.max(0, v))
    },

    pricePreviewTotal() {
      return Math.max(0, this.pricePreviewSubtotal - this.pricePreviewDiscount)
    },

    remaining() {
      return Math.max(0, this.amountDue - this.totalPaid)
    },

    overpayment() {
      return Math.max(0, this.totalPaid - this.amountDue)
    },
  },

  watch: {
    'form.customer_id'(customerId) {
      if (customerId) this.loadWalletBalance(customerId)
      else this.walletBalance = null
    },
  },

  methods: {
    formatPickerDate(d) {
      if (!d) return '';
      return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    },

    // ── Public API ────────────────────────────────────────────────────────

    show() {
      this.loadPackages()
      this.loadPaymentMethods()
      this.loadWelcomeDefault()
      this.$refs.modal.show()
    },

    async loadWelcomeDefault() {
      try {
        const { data } = await axios.get('/api/subscriptions/member-settings', { _silent: true })
        this.welcomeSmsDefault     = !!data.settings.welcome_sms_enabled
        this.form.send_welcome_sms = this.welcomeSmsDefault
      } catch (e) { /* no access - leave unticked */ }
    },

    // ── Data loading ──────────────────────────────────────────────────────

    async loadPackages() {
      try {
        const { data } = await axios.get('/api/subscription-packages')
        this.packages = data.filter(p => p.is_active)
        this.packageOptions = [
          { value: null, text: '— Select package —' },
          ...this.packages.map(p => ({
            value: p.id,
            text:  `${p.name} — KES ${this.formatAmount(p.price)}`,
          })),
        ]
      } catch (e) { console.error('Failed to load packages', e) }
    },

    async createPackage() {
      this.packageFormError = ''
      this.creatingPackage  = true
      try {
        const { data } = await axios.post('/api/subscription-packages', { ...this.packageForm, is_active: true })
        await this.loadPackages()
        this.form.subscription_package_id = data.data.id
        this.$refs.newPackageModal.hide()
      } catch (e) {
        const errs = e.response?.data?.errors
        this.packageFormError = errs ? Object.values(errs)[0][0] : (e.response?.data?.message || 'Could not create the package.')
      } finally {
        this.creatingPackage = false
      }
    },

    resetPackageForm() {
      this.packageForm      = { name: '', product_id: null, price: null, duration_value: 1, duration_unit: 'month' }
      this.packageFormError = ''
      this.clearPackageProduct()
    },

    onProductSearch() {
      clearTimeout(this.productSearchTimer)
      if (this.productSearch.trim().length < 2) { this.productResults = []; return }
      this.searchingProducts = true
      this.productSearchTimer = setTimeout(async () => {
        try {
          const { data } = await axios.get('/api/products', {
            params: { search: this.productSearch.trim(), per_page: 10 },
            _silent: true,
          })
          this.productResults = (data.items || []).map(p => ({ id: p.id, product: p.product }))
        } catch (e) {
          this.productResults = []
        } finally {
          this.searchingProducts = false
        }
      }, 300)
    },

    pickPackageProduct(p) {
      this.packageForm.product_id = p.id
      this.packageProductName     = p.product
      // Name the package after the product if staff haven't typed one yet.
      if (!this.packageForm.name) this.packageForm.name = p.product
      this.productSearch  = ''
      this.productResults = []
    },

    clearPackageProduct() {
      this.packageForm.product_id = null
      this.packageProductName     = ''
      this.productSearch          = ''
      this.productResults         = []
    },

    async loadPaymentMethods() {
      try {
        const { data } = await axios.get('/api/payment-methods')
        this.paymentMethods = [
          { value: null, text: '— Select method —' },
          ...(data.data || data).map(m => ({
            value: m.id,
            text: m.payment_method + (m.account_label ? ' · ' + m.account_label : m.account_number ? ' · ' + m.account_number : ''),
          })),
        ]
      } catch (e) { console.error('Failed to load payment methods', e) }
    },

    async loadWalletBalance(customerId) {
      try {
        const { data } = await axios.get(`/api/subscriptions/wallet/${customerId}`)
        this.walletBalance = parseFloat(data.balance || 0)
      } catch {
        this.walletBalance = 0
      }
    },

    // ── Inline customer creation ──────────────────────────────────────────

    showNewCustomerModal() {
      this.$refs.newCustomerModal.show()
    },

    async createCustomer() {
      this.customerFormError = null
      this.creatingCustomer  = true
      try {
        const { data } = await axios.post('/api/newcustomer', this.customerForm)
        const customer = data.customer || data

        this.form.customer_id = customer.id
        this.$refs.customerSearch.selectedOption = customer

        this.$refs.newCustomerModal.hide()
        this.$bvToast.toast(`${customer.full_name} added and selected.`, {
          title: 'Customer Created', variant: 'success',
        })
      } catch (e) {
        this.customerFormError =
          e.response?.data?.message || 'Failed to create customer. Please try again.'
      } finally {
        this.creatingCustomer = false
      }
    },

    resetCustomerForm() {
      this.customerForm      = { f_name: '', l_name: '', phone_no: '', c_email: '', krapin: '', company: '' }
      this.customerFormError = null
      this.creatingCustomer  = false
    },

    // ── Step 1 — Create subscription (no payment) ─────────────────────────

    async createSubscription() {
      this.errors = {}

      if (!this.form.customer_id) {
        this.errors.customer_id = 'Please select a customer.'
        return
      }
      if (!this.form.subscription_package_id) {
        this.errors.subscription_package_id = 'Please select a package.'
        return
      }

      this.creatingSubscription = true
      try {
        const { data } = await axios.post('/api/subscriptions/create-only', this.form)

        this.subscriptionId = data.data.subscription.id
        this.amountDue      = data.data.amount_due
        this.totalPaid      = 0
        this.step           = 2

      } catch (e) {
        this.$bvToast.toast(
          e.response?.data?.message || 'Failed to create subscription.',
          { title: 'Error', variant: 'danger' }
        )
      } finally {
        this.creatingSubscription = false
      }
    },

    // ── Step 2 — Post a payment line ──────────────────────────────────────

    async postPaymentLine() {
      this.paymentErrors = {}

      if (!this.paymentLine.payment_type_id) {
        this.paymentErrors.payment_type_id = 'Select a payment method.'
        return
      }
      if (!this.paymentLine.amount || this.paymentLine.amount <= 0) {
        this.paymentErrors.amount = 'Enter a valid amount.'
        return
      }

      this.postingPayment = true
      try {
        const { data } = await axios.post(
          `/api/beta-pay/${this.subscriptionId}/payment`,
          {
            payment_type_id: this.paymentLine.payment_type_id,
            amount:          this.paymentLine.amount,
          }
        )

        // Record in local list for display
        const methodLabel = this.paymentMethods.find(
          m => m.value === this.paymentLine.payment_type_id
        )?.text || 'Payment'

        this.postedPayments.push({
          method_label: methodLabel,
          amount:       this.paymentLine.amount,
        })

        this.totalPaid     = data.total_paid
        this.overpaymentHandled = false

        // Reset input
        this.paymentLine = { payment_type_id: null, amount: null }

      } catch (e) {
        this.$bvToast.toast(
          e.response?.data?.message || 'Failed to record payment.',
          { title: 'Error', variant: 'danger' }
        )
      } finally {
        this.postingPayment = false
      }
    },

    // ── Wallet payment ────────────────────────────────────────────────────

    onWalletToggle(checked) {
      this.showWalletPrompt = checked
    },

    async postWalletPayment() {
      const amount = Math.min(this.remaining, this.walletBalance)
      this.postingPayment = true
      try {
        const { data } = await axios.post(
          `/api/beta-pay/${this.subscriptionId}/payment/wallet`,
          { amount }
        )

        this.postedPayments.push({
          method_label: 'Wallet',
          amount,
        })

        this.totalPaid      = data.total_paid
        this.walletBalance  = data.wallet_balance
        this.useWallet      = false
        this.showWalletPrompt = false
        this.overpaymentHandled = false

      } catch (e) {
        this.$bvToast.toast(
          e.response?.data?.message || 'Wallet payment failed.',
          { title: 'Error', variant: 'danger' }
        )
      } finally {
        this.postingPayment = false
      }
    },

    // ── Overpayment to wallet ─────────────────────────────────────────────

    async loadOverpaymentToWallet() {
      this.loadingOverpayment = true
      try {
        await axios.post(
          `/api/beta-pay/${this.subscriptionId}/payment/overpayment-to-wallet`,
          { amount: this.overpayment }
        )
        this.$bvToast.toast(
          `KES ${this.formatAmount(this.overpayment)} loaded to wallet.`,
          { title: 'Wallet Loaded', variant: 'success' }
        )
        this.overpaymentHandled = true
        this.walletBalance = (this.walletBalance || 0) + this.overpayment
      } catch (e) {
        this.$bvToast.toast(
          e.response?.data?.message || 'Failed to load overpayment.',
          { title: 'Error', variant: 'danger' }
        )
      } finally {
        this.loadingOverpayment = false
      }
    },

    // ── Complete ──────────────────────────────────────────────────────────

    complete() {
      this.$bvToast.toast('Subscription created successfully.', {
        title: 'Success', variant: 'success',
      })
      this.$emit('created')
      this.$refs.modal.hide()
    },

    skipPayment() {
      this.$bvToast.toast('Subscription saved. Outstanding balance can be collected later.', {
        title: 'Subscription Created', variant: 'info',
      })
      this.$emit('created')
      this.$refs.modal.hide()
    },

    // ── Reset ─────────────────────────────────────────────────────────────

    resetForm() {
      this.step = 1
      this.form = {
        customer_id: null, subscription_package_id: null,
        start_date: null, is_auto_renew: false, notes: null,
        participant_count: 1, discount_type: 'flat', discount_value: 0,
        send_welcome_sms: this.welcomeSmsDefault,
      }
      this.errors              = {}
      this.creatingSubscription = false
      this.subscriptionId      = null
      this.amountDue           = 0
      this.totalPaid           = 0
      this.postedPayments      = []
      this.paymentLine         = { payment_type_id: null, amount: null }
      this.paymentErrors       = {}
      this.postingPayment      = false
      this.useWallet           = false
      this.showWalletPrompt    = false
      this.overpaymentHandled  = false
      this.loadingOverpayment  = false
      this.walletBalance       = null
      this.resetCustomerForm()
    },

    // ── Helpers ───────────────────────────────────────────────────────────

    formatAmount(v) {
      return parseFloat(v || 0).toLocaleString('en-KE', {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      })
    },
  },
}
</script>
